import { ContactResponse } from '@business/entities/contact';
import { GmailMessageId, GmailMessageResponse, GmailThreadId, GmailThreadResponse } from '@business/entities/gmail_messages';
import { Button } from '@core/components/Button';
import { TopLoadingBar } from '@core/components/LoadingBar';
import { ModalContent, ModalDrawer, ModalHeader } from '@core/components/Modal';
import { useBloc } from '@core/utils/bloc';
import { Formatter } from '@core/utils/formatter';
import { useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { toMoment } from '@core/utils/time';
import { css } from '@emotion/react';
import { EmailFromToCard } from '@modules/notes/TabNotes/EmailTimeLineItem';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardBody, Collapse } from 'reactstrap';
import { NewEmail } from '../NewEmail';
import { EmailOverlayBloc } from './EmailOverlayBloc';

const styles = {
  content: css`
    padding: 25px !important;
    background-color: var(--body-bg-color);
  `,

  title: css`
    .MuiTypography-root {
      height: 65px;
      align-items: center;
      position: relative;
      padding: 0 10px 0 20px;
    }
  `,
};

interface EmailSingleThreadProps {
  thread: GmailThreadResponse;
  contacts?: ContactResponse[];
}

const EmailSingleThread = memo((props: EmailSingleThreadProps) => {
  const { thread, contacts } = props;
  return (
    <Card style={{ padding: 20, width: '100%' }} id={`thread-${thread.id}`} key={thread.id}>
      <div className="d-flex flex-row" style={{ justifyContent: 'space-between' }}>
        <span className="text-primary d-flex flex-row">
          <div className="avatar-group" style={{ marginRight: 10 }}>
            <div className="avatar-group-item">
              <div className="avatar-xs">
                <span className={`avatar-title rounded-circle bg-info text-white font-size-16`}>
                  <i className="bx bx-user font-size-16"></i>
                </span>
              </div>
            </div>
          </div>
          <div>
            {thread.from.length > 0 && (
              <div>
                From: <EmailFromToCard emails={thread.from} contacts={contacts} />
              </div>
            )}
            {thread.to.length > 0 && (
              <div>
                To : <EmailFromToCard emails={thread.to} contacts={contacts} />
              </div>
            )}
            {thread.cc.length > 0 && (
              <div>
                CC : <EmailFromToCard emails={thread.cc} contacts={contacts} />
              </div>
            )}
            {thread.bcc.length > 0 && (
              <div>
                BCC : <EmailFromToCard emails={thread.bcc} contacts={contacts} />
              </div>
            )}
          </div>
        </span>
        <span className={'primary-text'}>
          {Formatter.formatTime(thread.createdAt, 'LLL')} ({moment.duration(toMoment(thread.createdAt).diff(moment())).humanize()} ago)
          <br />
        </span>
      </div>
      <CardBody>
        <iframe srcDoc={thread.html} style={{ width: '100%', height: '75vh', border: 'none' }}></iframe>
        {/* <div dangerouslySetInnerHTML={{ __html: thread.html }}></div> */}
      </CardBody>
    </Card>
  );
});

export interface EmailOverlayProps {
  emailId: GmailMessageId;
  threadId?: GmailThreadId;
  contacts?: ContactResponse[];
  onClose?(): void;
}

export const EmailOverlay = memo((props: EmailOverlayProps) => {
  const { emailId, contacts, threadId = '' } = props;
  const bloc = useBloc(EmailOverlayBloc, emailId);
  const email = useObservable(bloc.email$) as GmailMessageResponse | null;
  const loading = useLoadingState(bloc.loading);
  const [isReply, setIsReply] = useState<boolean>(false);

  const replyRef = useRef(null);

  const emailItems = useMemo(() => {
    if (email) {
      return R.concat(email.to, email.cc, email.bcc);
    }
    return [];
  }, [email]);

  const replyBoxScroll = useCallback(() => {
    if (!email) {
      return;
    }
    const replyBox = document.querySelector(`#replyBox-${email.id}`);
    setTimeout(() => {
      replyBox && replyBox.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 500);
  }, [email]);

  useEffect(() => {
    if (threadId !== '' && email?.threads && !!email.threads.length) {
      setTimeout(() => {
        const section = document.getElementById(`thread-${threadId}`);
        section && section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  }, [email, threadId]);

  if (loading.loading || !email) return <TopLoadingBar />;

  return (
    <ModalDrawer maxWidth="lg" onClose={props.onClose}>
      <ModalHeader customCss={styles.title} onClose={props.onClose}>
        <div className="d-flex flex-row" style={{ justifyContent: 'space-between' }}>
          <h4>
            {email.subject}
            {email.from.length > 0 && (
              <div style={{ fontSize: 13 }}>
                From: <EmailFromToCard emails={email.from} contacts={contacts} />
              </div>
            )}
          </h4>
          <div>
            <Button
              onClick={() => {
                setIsReply(true);
                replyBoxScroll();
              }}
              style={{ float: 'right' }}
            >
              <i className="bx bx-reply font-size-16"></i> Reply
            </Button>
          </div>
        </div>
      </ModalHeader>
      <ModalContent customCss={styles.content}>
        <div className="d-flex flex-column">
          {email.threads.map((thread, index) => {
            return <EmailSingleThread key={`thread-${index}`} thread={thread} contacts={contacts} />;
          })}
          <Card id={`replyBox-${email.id}`} style={{ padding: 20, flex: 1, display: `${!!isReply ? '' : 'none'}` }}>
            <CardBody>
              <Collapse isOpen={isReply}>
                <NewEmail emails={emailItems} email={email} />
              </Collapse>
            </CardBody>
          </Card>
        </div>
      </ModalContent>
    </ModalDrawer>
  );
});
